// src/generators/dzial2_algebra/temat3_nierownosci/podtemat3_wartosc_bezwzgledna_types/AbsLinearInequalityShiftedScaledTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import TwoRangesOverlap from "../../../../components/visuals/TwoRangesOverlap";

/* ================= helpers ================= */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };
const invertSign = (k) => ({ lt: "gt", gt: "lt", le: "ge", ge: "le" }[k]);

function absLatex(A, B) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : A > 0 ? `${A}x` : `-${Math.abs(A)}x`;
  const bx = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;
  return `\\left|${ax} ${bx}\\right|`.replace(/\s+/g, " ").trim();
}

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; }
function toFrac(num, den) { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { n: num / g, d: den / g }; }
function fracToLatex({ n, d }) {
  const s = n < 0 ? "-" : "";
  const an = Math.abs(n);
  const int = Math.floor(an / d);
  const rem = an % d;
  if (rem === 0) return `${s}${int}`;
  return int === 0 ? `${s}\\dfrac{${rem}}{${d}}` : `${s}${int}\\dfrac{${rem}}{${d}}`;
}
function orderedBounds(A, B, c) {
  const vL = (-c - B) / A, vR = (c - B) / A;
  const texL = fracToLatex(toFrac(-c - B, A));
  const texR = fracToLatex(toFrac(c - B, A));
  return vL <= vR ? { lowNum: vL, highNum: vR, lowTex: texL, highTex: texR }
                  : { lowNum: vR, highNum: vL, lowTex: texR, highTex: texL };
}

/* ===== rozwiązanie dla c>0 ===== */
function solutionForPositiveC({ A, B, c, kind }) {
  // c > 0 jest gwarantowane przez generator
  if (kind === "lt" || kind === "le") {
    const L = (-c - B) / A, R = (c - B) / A, inc = kind === "le";
    return { kind: "between", seg: { L: Math.min(L, R), R: Math.max(L, R), Lclosed: inc, Rclosed: inc } };
  }
  const inc = kind === "ge";
  const b1 = ( c - B) / A;
  const b2 = (-c - B) / A;
  const r1 = Math.min(b1, b2);
  const r2 = Math.max(b1, b2);
  return { kind: "outside", inclusive: inc, left: { L: -Infinity, R: r1, Rclosed: inc }, right: { L: r2, R: Infinity, Lclosed: inc } };
}

/* ================= generator =================
   Losujemy parametry tak, by po przekształceniu dostać c > 0. */
export function generateTask() {
  const randi = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
  const pick = (arr) => arr[randi(0, arr.length - 1)];

  // A ≠ 0, α ≠ 0 (α może być ujemne — pokażemy odwrócenie znaku)
  let A = 0; while (A === 0) A = randi(-8, 8);
  const B = randi(-12, 12);
  const alpha = pick([-7,-6,-5,-4,-3,-2, 2,3,4,5,6,7]);
  const c = randi(1, 10); // zawsze dodatnie
  // Ustawiamy beta i gamma tak, by po odjęciu beta i podzieleniu przez alpha wyszło c
  let beta = 0; while (beta === 0) beta = randi(-12, 12);
  const gamma = alpha * c + beta;

  const kind = pick(SIGN_KINDS);
  const kind_init = alpha >= 0 ? kind : invertSign(kind); // zanim podzielimy przez α
  return { type: "AbsLinearInequalityShiftedScaledTask", data: { A, B, alpha, beta, gamma, kind_init, kind, c } };
}

/* ================= UI ================= */
export function renderTask({ data }) {
  const { A, B, alpha, beta, gamma, kind_init } = data;
  const left = `${alpha}\\,${absLatex(A,B)} ${beta===0?"":beta>0?`+ ${beta}`:`- ${Math.abs(beta)}`}`;
  const latex = `${left}\\;${SIGN_LATEX[kind_init]}\\;${gamma}`;
  return (<p>Rozwiąż nierówność: <InlineMath math={latex} /></p>);
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}><InlineMath math={'x\\;\\in'} /></div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2) ∪ (5, ∞) • (-3, 7) • [1, 4] • {3} • ℝ • ∅"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== porównanie odpowiedzi ===== */
export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionForPositiveC({ A: data.A, B: data.B, c: data.c, kind: data.kind });

  function intervalsEqualFlexible(expected, parsed) {
    if (!parsed) return false;
    if (parsed.kind !== "intervals" && parsed.kind !== "all" && parsed.kind !== "empty" && parsed.kind !== "point") return false;

    if (expected.kind === "between") {
      if (parsed.kind !== "intervals" || parsed.intervals.length !== 1) return false;
      const I = parsed.intervals[0];
      const eq = (x, y, tol=1e-9) => Math.abs(x - y) <= tol;
      return eq(I.left, expected.seg.L) && eq(I.right, expected.seg.R) &&
             !!I.leftClosed === !!expected.seg.Lclosed && !!I.rightClosed === !!expected.seg.Rclosed;
    }

    if (expected.kind === "outside") {
      if (parsed.kind !== "intervals" || parsed.intervals.length !== 2) return false;
      const [L, R] = parsed.intervals;
      const eq = (x, y, tol=1e-9) => Math.abs(x - y) <= tol;
      const leftOK  = L.left === -Infinity && eq(L.right, expected.left.R)  && !!L.rightClosed === !!expected.left.Rclosed;
      const rightOK = R.right ===  Infinity && eq(R.left, expected.right.L) && !!R.leftClosed  === !!expected.right.Lclosed;
      return leftOK && rightOK;
    }
    return false;
  }
  return { correct: intervalsEqualFlexible(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const { A, B, c, kind } = data;
  const ans = solutionForPositiveC({ A, B, c, kind });
  const { lowTex, highTex } = orderedBounds(A, B, c);

  if (ans.kind === "between") {
    const lb = ans.seg.Lclosed ? "[" : "("; const rb = ans.seg.Rclosed ? "]" : ")";
    return <InlineMath math={`x\\in ${lb}${lowTex},\\; ${highTex}${rb}`} />;
  }
  const rb = ans.inclusive ? "]" : ")"; const lb = ans.inclusive ? "[" : "(";
  return <InlineMath math={`x\\in (-\\infty,\\; ${lowTex}${rb}\\;\\cup\\; ${lb}${highTex},\\; \\infty)`} />;
}

/* ===== kroki/wyjaśnienie ===== */

// Kroki dla nierówności liniowej (używane w Warunku 1/2)
function solveLinearSide(A, B, rel, c) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : `${A}x`;
  const btxt = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;

  // Linia 1: dopisujemy na końcu operację dodawania/odejmowania stałej B
  const opB = B === 0 ? "" : String.raw`\;\Big|\, ${B > 0 ? "-" : "+"}\, ${Math.abs(B)}`;
  const line1 = String.raw`${ax} ${btxt} \; ${rel} \; ${c}${opB}`;

  // Linia 2: po redukcji stałej dopisujemy na końcu dzielenie przez A
  const rhs2 = B === 0 ? c : (B > 0 ? `${c - B}` : `${c + Math.abs(B)}`);
  const line2 = String.raw`${ax} \; ${rel} \; ${rhs2}\;\Big|:\, ${A}`;

  // Linia 3: po podzieleniu (z ewentualnym odwróceniem znaku)
  const flip = A < 0;
  const rel3 = flip ? (rel === "<" ? ">" : rel === ">" ? "<" : rel === "≤" ? "≥" : "≤") : rel;
  const { n, d } = toFrac(Number(rhs2), A);
  const line3 = String.raw`x \; ${rel3} \; ${fracToLatex({ n, d })}`;

  return String.raw`\begin{aligned}
${line1}\\[2pt]
${line2}\\[2pt]
${line3}
\end{aligned}`;
}

export function renderExplanation({ data }) {
  const { A, B, alpha, beta, gamma, kind_init, kind, c } = data;

  const Center = ({ children }) => (
    <div style={{ display: "block", width: "100%", minWidth: "100%", alignSelf: "stretch", justifySelf: "stretch" }}>
      <div style={{ margin: "0 auto", display: "table" }}>{children}</div>
    </div>
  );

  // Krok 1 — globalne przekształcenie do |Ax+B| (z adnotacjami po prawej)
  const left0 = `${alpha}\\,${absLatex(A,B)} ${beta===0?"":beta>0?`+ ${beta}`:`- ${Math.abs(beta)}`}`;
  const eq1 = `${left0}\\;${SIGN_LATEX[kind_init]}\\;${gamma}`;
  const right2 = gamma - beta; // = alpha * c
  const eq2 = `${alpha}\\,${absLatex(A,B)}\\;${SIGN_LATEX[kind_init]}\\;${right2}`;
  const finalSign = alpha >= 0 ? kind_init : invertSign(kind_init);
  const eq3 = `${absLatex(A,B)}\\;${SIGN_LATEX[finalSign]}\\;${c}`;

  const addOpBeta = beta === 0 ? "" : String.raw`\;\Big|\, ${beta > 0 ? "-" : "+"}\, ${Math.abs(beta)}`;
  const eq1Annotated = `${eq1}${addOpBeta}`;
  const eq2Annotated = String.raw`${eq2}\;\Big|:\, ${alpha}`;

  // Przygotowanie granic i tekstów
  const { lowNum, highNum, lowTex, highTex } = orderedBounds(A, B, c);

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Rozwiązujemy nierówność — upraszczamy i dzielimy (gdy dzielimy przez ujemną liczbę, odwracamy znak).</p>

      <Center>
        <BlockMath math={String.raw`
\begin{aligned}
${eq1Annotated}\\[4pt]
${eq2Annotated}\\[2pt]
${eq3}
\end{aligned}
        `} />
      </Center>

      {alpha < 0 && (
        <div style={{ textAlign: "center", marginTop: "0.25rem" }}>
          <InlineMath math={`\\text{Dzielimy przez liczbę ujemną } ${alpha} \\;\\Rightarrow\\; \\text{ odwracamy znak.}`}/>
        </div>
      )}

      <p><strong>Krok 2:</strong> Rozbijamy wartość bezwzględną i rozwiązujemy dwie nierówności liniowe.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", alignItems: "start" }}>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {(kind==="lt"||kind==="le") ? "Lewa (koniunkcja)" : "Warunek 1"}
          </div>
          <Center>
            <BlockMath math={solveLinearSide(A, B, (kind==="le"||kind==="ge") ? "≥" : ">", (kind==="lt"||kind==="le") ? -c :  c)} />
          </Center>
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {(kind==="lt"||kind==="le") ? "Prawa (koniunkcja)" : "Warunek 2"}
          </div>
          <Center>
            <BlockMath math={solveLinearSide(A, B, (kind==="le"||kind==="ge") ? "≤" : "<", (kind==="lt"||kind==="le") ?  c : -c)} />
          </Center>
        </div>
      </div>

      {/* Wizualizacja: ten sam styl i kolor overlapa co w wersji AllCases */}
      <div style={{ maxWidth: 700, margin: "0.5rem auto" }}>
        {(kind==="lt"||kind==="le") ? (
          <TwoRangesOverlap
            showZero
            lockPair
            pairFractions={[1/3, 2/3]}
            overlapColorPreset="pink"
            overlapOpacity={0.4}
            overlapBlur={2}
            ranges={[
              { to:   highNum, rightClosed: kind === "le", rightLabelLatex: highTex },
              { from: lowNum,  leftClosed:  kind === "le", leftLabelLatex:  lowTex  },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to:   lowNum,  rightClosed: kind === "ge", rightLabelLatex: lowTex  },
              { from: highNum, leftClosed:  kind === "ge", leftLabelLatex:  highTex },
            ]}
          />
        )}
      </div>

      <p><strong>Krok 3:</strong> Zapisujemy rozwiązanie.</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        { (kind==="lt"||kind==="le")
          ? <InlineMath math={`x\\in ${kind==="le" ? "[" : "("}${lowTex},\\; ${highTex}${kind==="le" ? "]" : ")"}`} />
          : <InlineMath math={`x\\in (-\\infty,\\; ${lowTex}${kind==="ge" ? "]" : ")"}\\;\\cup\\; ${kind==="ge" ? "[" : "("}${highTex},\\; \\infty)`} />
        }
      </div>
    </div>
  );
}
